/**
 * How a Hub room loads and scrolls (Kyu, 7 Oct 2026: open at the newest
 * message, older ones on scroll up, like Slack).
 *
 * A room reads its newest CHAT_PAGE messages. Scrolling to the old edge asks
 * for CHAT_PAGE more. The query is always "the newest N", so a refresh never
 * leaves a hole in the middle. Display only: no database change.
 *
 * The message list is a flex column-reverse box (messages.css .message-list),
 * so the browser measures its scroll from the NEWEST end: scrollTop 0 is the
 * newest message and scrolling up gives a negative scrollTop. That is why it
 * opens at the latest message and keeps the reader's place when older rows
 * arrive: neither needs a line of script. The Agents room is the exception
 * (newestFirst.ts: newest on top), where scrollTop 0 is the top, the newest.
 */
export const CHAT_PAGE = 25;
/** The most a room ever loads at once. A link to a missing message stops here. */
export const CHAT_MAX = 1000;
/** Within this many pixels of an edge counts as "at" the edge. */
export const EDGE_PX = 120;

interface Box {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

/** Pixels between the view and the newest message. 0 means looking at it. */
export function latestGap(box: Pick<Box, 'scrollTop'>, newestFirst: boolean): number {
  return newestFirst ? box.scrollTop : Math.max(0, -box.scrollTop);
}

/** Pixels left to scroll before the oldest loaded message. */
export function oldestGap(box: Box, newestFirst: boolean): number {
  return box.scrollHeight - box.clientHeight - latestGap(box, newestFirst);
}

/** The next window: 25 more when the reader scrolls, double when a link needs a message. */
export function nextLimit(limit: number, jump = false): number {
  return Math.min(jump ? limit * 2 : limit + CHAT_PAGE, CHAT_MAX);
}

/** True when the last read filled its window, so older messages may exist. */
export function mayHaveOlder(loaded: number, limit: number): boolean {
  return loaded >= limit && limit < CHAT_MAX;
}
